import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { RoleType, StorageKey } from "../../App/enums"
import { selectTranslations } from "../../App/i18n/i18nSlice"
import SessionService from "../../App/SessionService"
import { RootState } from "../../App/store"
import { Calendar, CalendarActive } from "../Shared/Components/SvgIcons"
import UpcomingEventsDropdownItem from "./UpcomingEventsDropdownItem"

function UpcomingEventsDropdown() {
    const navigate = useNavigate()
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const events = useSelector((state: RootState) => state.upcomingEventsSlice.upcomingEvents)
    const translation = useSelector(selectTranslations)
    const [notificationsOpen, setNotificationsOpen] = useState(false)
    const { id } = useParams()
    const orgId = SessionService.getItem(StorageKey.organizationId)
    const userOffset = new Date().getTimezoneOffset() * 60 * 1000
    const notificationFilterNumber = 5
    const lastNotificationIndex = notificationFilterNumber - 1
    let notificationRef = React.useRef<HTMLInputElement>(null)

    const getNormalizedEventDate = useCallback((date: string) => new Date(new Date(date).getTime() - userOffset), [userOffset])

    const sortedEvents = useMemo(() => {
        return [...events]
            .filter((event) => !Number.isNaN(getNormalizedEventDate(event.start_date).getTime()))
            .sort((firstEvent, secondEvent) => {
                return getNormalizedEventDate(firstEvent.start_date).getTime() - getNormalizedEventDate(secondEvent.start_date).getTime()
            })
    }, [events, getNormalizedEventDate])

    const visibleEvents = useMemo(() => {
        return sortedEvents.slice(0, notificationFilterNumber)
    }, [sortedEvents])

    const hasEvents = sortedEvents.length > 0
    const hasUpcomingEvents = useMemo(() => {
        const currentDate = new Date(new Date().getTime() - userOffset)
        const threeDaysFromNow = new Date(currentDate)
        threeDaysFromNow.setDate(currentDate.getDate() + 3)

        return sortedEvents.some((event) => {
            const eventDate = getNormalizedEventDate(event.start_date)
            return eventDate > currentDate && eventDate <= threeDaysFromNow
        })
    }, [sortedEvents, getNormalizedEventDate, userOffset])

    useEffect(() => {
        const onMouseDownHandler = (event: Event) => {
            if (notificationsOpen && notificationRef.current && !notificationRef.current.contains(event.target as HTMLElement)) {
                setNotificationsOpen(false)
            }
        }
        document.addEventListener("mousedown", onMouseDownHandler)

        return () => {
            document.removeEventListener("mousedown", onMouseDownHandler)
        }
    }, [notificationsOpen])

    const onEventClick = (eventId: number, roomId: number) => {
        switch (role) {
            case RoleType.USER:
                navigate(`/room/${roomId}?tab=events&event=${eventId}`)
                break
            case RoleType.ORGANIZATION:
                navigate(`/room/${id!}/${roomId}?tab=events&event=${eventId}`)
                break
            default:
                break
        }

        setNotificationsOpen(false)
    }

    const onRoomClick = (roomId: number) => {
        switch (role) {
            case RoleType.USER:
                navigate(`/room/${roomId}`)
                break
            case RoleType.ORGANIZATION:
                navigate(`/room/${id!}/${roomId}`)
                break
            default:
                break
        }

        setNotificationsOpen(false)
    }

    const onRouteChange = () => {
            switch (role) {
                case RoleType.USER:
                    const userPath = "/upcoming-events"
                    navigate(userPath)
                    window.scrollTo(0, 0)
                    break
                case RoleType.ORGANIZATION:
                    if (id === orgId) {
                        const organizationPath = `/upcoming-events/${id}`
                        navigate(organizationPath)
                        window.scrollTo(0, 0)
                    } else {
                        const organizationPath = `/upcoming-events/${orgId}`
                        navigate(organizationPath)
                        window.scrollTo(0, 0)
                    }
    
                    break
                default:
                    break
            }
        }

    const onLastItem = (index: number) => {
        if (visibleEvents.length < notificationFilterNumber) {
            return visibleEvents.length - 1 === index
        } else {
            return index === lastNotificationIndex
        }
    }

    return (
        <div className="header__menu-notifications" ref={notificationRef}>
            <div
                className="header__icon"
                onClick={() => {
                    setNotificationsOpen(!notificationsOpen)
                }}
            >
                {!hasUpcomingEvents ? <Calendar /> : <CalendarActive />}
            </div>
            {notificationsOpen && (
                <div className="notifications-dropdown">
                    <div className="notifications-dropdown__event-content">
                        <div className="notifications-dropdown__upper-panel">
                            {hasEvents ? (
                                visibleEvents.map((event, index) => {
                                    return (
                                        <UpcomingEventsDropdownItem
                                            isLastItem={onLastItem(index)}
                                            name={event.title}
                                            key={event.id}
                                            id={event.id}
                                            format={event.formats}
                                            location={event.location_type}
                                            date={event.start_date}
                                            onClick={() => onEventClick(event.id, event.room.id)}
                                            onRoomClick={() => onRoomClick(event.room.id)}
                                            roomId={event.room.id}
                                            roomName={event.room.name}
                                            isDropDown={true}
                                            isSoon={index < 2}
                                        />
                                    )
                                })
                            ) : (
                                <div className="notifications-dropdown__empty-state">
                                    <div className="notifications-dropdown__empty-icon"><Calendar /></div>
                                    <div className="notifications-dropdown__text">{translation.youHaveNoUpcomingEvents}</div>
                                </div>
                            )}
                        </div>
                        <div className="notifications-dropdown__lower-panel">
                            <button type="button" className="notifications-dropdown__link" onClick={onRouteChange}>
                                {translation.seeAllResults}
                            </button>
                        </div>
                    </div>
                </div>
            )}{" "}
        </div>
    )
}

export default UpcomingEventsDropdown
